import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Control, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Hook, rossumStore } from '@rossum/api-client/hooks';
import {
  IconEye,
  IconEyeOff,
  IconInfoSquareRounded,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Paper,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../../components/ReactHookForm/controls/TextFieldControl';
import ValidationInput from '../../../../components/ReactHookForm/ValidationInput';
import {
  docsLinks,
  EXTERNAL_CRONTAB_VALIDATOR,
} from '../../../../constants/values';
import { italicText, linebreak, link } from '../../../../lib/formaterValues';
import { sanitizeLinks } from '../../../../lib/htmlSanitization';
import { useIsFeatureEnabled } from '../../../feature-enablement/featureEnablement';
import { CredentialsMultiSelect } from '../../../integration-credentials/ui/CredentialsMultiSelect';
import { QueueMultiSelect } from '../../../queues/select-queue/QueueMultiSelect';
import { EventMultiSelect } from '../../components/EventMultiSelect';
import { isPublicFunction, isPublicWebhook } from '../../helpers';
import { getIcon } from '../../lib/helpers';
import { HookInterfaces } from './components/HookInterfaces';
import { InvokeButton } from './components/InvokeButton';
import { ViewCodePart } from './components/ViewCodePart';
import { ExtensionData } from './helpers';

DOMPurify.addHook('afterSanitizeAttributes', sanitizeLinks);

type GeneralDetailSettingsProps = {
  detailUrl: string;
  control: Control<ExtensionData, unknown>;
  selectedExtension: Hook;
  events: ExtensionData['events'];
  noChanges: boolean;
  eventsHasError: boolean;
};

export const GeneralDetailSettings = ({
  selectedExtension,
  detailUrl,
  control,
  events,
  noChanges,
  eventsHasError,
}: GeneralDetailSettingsProps) => {
  const intl = useIntl();

  const [showSecret, setShowSecret] = useState(false);

  const isFromStore = selectedExtension.extensionSource === rossumStore;

  const isWebhookPublic = isPublicWebhook(selectedExtension);

  const isCredentialsEnabled = useIsFeatureEnabled('integration-credentials');

  return (
    <>
      {isFromStore && (
        <Stack spacing={1} component={Paper} elevation={8} sx={{ p: 2 }}>
          <Stack spacing={1} direction="row" alignItems="center">
            {getIcon(
              selectedExtension.type,
              isPublicFunction(selectedExtension)
                ? selectedExtension.config?.runtime
                : undefined
            )}
            <Typography fontWeight="bold">{selectedExtension.name}</Typography>
          </Stack>
          <Typography variant="body2" color="secondary">
            {selectedExtension.description}
          </Typography>
        </Stack>
      )}
      {isFromStore && selectedExtension.guide && (
        <Stack
          component={Paper}
          spacing={1}
          elevation={8}
          sx={{
            p: 2,
            my: 3,
            '& button': {
              color: t => `${t.palette.primary.contrastText} !important`,
              backgroundColor: t => `${t.palette.primary.main} !important`,
            },
          }}
        >
          <Typography data-cy="extension-guide-title" fontWeight="bold">
            <FormattedMessage id="containers.settings.extensions.guide" />
          </Typography>
          <Box
            sx={{
              color: t => t.palette.text.secondary,
              fontSize: t => t.typography.body2.fontSize,
              '& iframe': {
                border: 'none',
                maxWidth: '100%',
              },
              a: {
                color: t => t.palette.primary.main,
                fontWeight: 'bold',
                textDecoration: 'underline',
              },
            }}
            data-cy="extension-guide-content"
            // oxlint-disable-next-line react/no-danger -- oxlint stricter than eslint — sanitized HTML, pre-existing usage
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(selectedExtension.guide, {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['allow', 'allowfullscreen'],
              }),
            }}
          />
        </Stack>
      )}
      {!isFromStore && (
        <ViewCodePart
          selectedExtension={selectedExtension}
          isFromStore={isFromStore}
          routeUrl={detailUrl}
        />
      )}
      <Stack spacing={1} width={1}>
        <Typography variant="body2">
          <FormattedMessage
            id={
              isFromStore
                ? 'containers.settings.extensions.createExtension.yourName.label'
                : 'containers.settings.extensions.createExtension.name.label'
            }
          />
        </Typography>
        <ValidationInput<ExtensionData>
          control={control}
          name="name"
          getErrorMessage={(_, { message }) => message ?? ''}
          placeholder={intl.formatMessage({
            id: `containers.settings.extensions.createExtension.name.placeholder`,
          })}
          dataCy="extensions-name-input"
        />
      </Stack>
      {!isFromStore && (
        <>
          <Stack spacing={1} width={1}>
            <Stack
              alignItems="flex-start"
              direction="row"
              justifyContent="space-between"
              alignContent="center"
            >
              <Typography variant="body2">
                <FormattedMessage id="containers.settings.extensions.createExtension.description.label" />
              </Typography>

              <Typography variant="body2" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.description.fieldDescription" />
              </Typography>
            </Stack>
            <TextFieldControl
              multiline
              size="small"
              minRows={4}
              ControllerProps={{ control, name: 'description' }}
              placeholder={intl.formatMessage({
                id: 'containers.settings.extensions.createExtension.description.placeholder',
              })}
              data-cy="extensions-description-input"
            />
          </Stack>
          <Stack spacing={1}>
            <Stack alignItems="flex-start" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">
                  <FormattedMessage id="containers.settings.extensions.createExtension.events.label" />
                </Typography>
                <Tooltip
                  title={
                    <FormattedMessage
                      id="containers.settings.extensions.function.events.tooltip"
                      values={{
                        link: link(docsLinks.webhookEvents),
                        linebreak,
                      }}
                    />
                  }
                  placement="top"
                >
                  <SvgIcon fontSize="medium" color="secondary">
                    <IconInfoSquareRounded />
                  </SvgIcon>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.events.subLabel" />
              </Typography>
            </Stack>
            <Controller
              control={control}
              name="events"
              render={({ field }) => <EventMultiSelect {...field} />}
            />
            {eventsHasError && (
              <Typography
                data-cy="error-message-eventsDropdown"
                variant="caption"
                color="error"
              >
                <FormattedMessage id="containers.settings.extensions.createExtension.events.required" />
              </Typography>
            )}
          </Stack>
        </>
      )}
      {events.includes('invocation.manual') && (
        <InvokeButton hookUrl={selectedExtension.url} noChanges={noChanges} />
      )}
      {(events.includes('invocation.integration') ||
        events.includes('invocation.interface')) && (
        <HookInterfaces hook={selectedExtension} />
      )}
      {events.includes('invocation.scheduled') && (
        <Stack spacing={1}>
          <Stack alignItems="flex-start" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2">
                <FormattedMessage id="containers.settings.extensions.createExtension.schedule.label" />
              </Typography>
              <Tooltip
                title={
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.schedule.tooltip"
                    values={{
                      link: link(EXTERNAL_CRONTAB_VALIDATOR),
                      linebreak,
                    }}
                  />
                }
                placement="top"
              >
                <SvgIcon fontSize="medium" color="secondary">
                  <IconInfoSquareRounded />
                </SvgIcon>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="secondary">
              <FormattedMessage id="containers.settings.extensions.createExtension.schedule.subLabel" />
            </Typography>
          </Stack>
          <ValidationInput
            control={control}
            name="schedule"
            placeholder="*/10 * * * *"
            dataCy="extensions-webhook-schedule-input"
            getErrorMessage={(_, { message }) => message ?? ''}
          />
        </Stack>
      )}
      <Stack spacing={1}>
        <Stack alignItems="flex-start" spacing={1}>
          <Typography variant="body2">
            <FormattedMessage id="containers.settings.extensions.createExtension.queues.label" />
          </Typography>
          <Typography variant="caption" color="secondary">
            <FormattedMessage
              id="containers.settings.extensions.createExtension.queues.subLabel"
              values={{
                linebreak,
                italicText,
              }}
            />
          </Typography>
        </Stack>
        <Controller
          control={control}
          name="queues"
          render={({ field }) => (
            <QueueMultiSelect {...field} withLabel={false} />
          )}
        />
      </Stack>
      {isCredentialsEnabled && (
        <Stack spacing={1}>
          <Stack alignItems="flex-start" spacing={1}>
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'containers.settings.extensions.createExtension.integrationCredentials.label',
              })}
            </Typography>
            <Typography variant="caption" color="secondary">
              {intl.formatMessage({
                id: 'containers.settings.extensions.createExtension.integrationCredentials.subLabel',
              })}
            </Typography>
          </Stack>
          <Controller
            control={control}
            name="integrationCredentials"
            render={({ field: { value, onChange } }) => (
              <CredentialsMultiSelect value={value} onChange={onChange} />
            )}
          />
        </Stack>
      )}

      {isWebhookPublic && (
        <>
          <Stack spacing={1}>
            <Stack alignItems="flex-start" spacing={1}>
              <Typography variant="body2">
                <FormattedMessage id="containers.settings.extensions.createExtension.webhook.url.label" />
              </Typography>
              <Typography variant="caption" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.webhook.url.subLabel" />
              </Typography>
            </Stack>
            <ValidationInput<ExtensionData>
              name="url"
              control={control}
              getErrorMessage={(_, { message }) => message ?? ''}
              dataCy="extensions-webhook-url-input"
              placeholder={intl.formatMessage({
                id: `containers.settings.extensions.createExtension.webhook.url.placeholder`,
              })}
            />
          </Stack>
          <Stack spacing={1} width={1}>
            <Stack
              alignItems="flex-start"
              direction="row"
              justifyContent="space-between"
            >
              <Stack alignItems="flex-start" spacing={1}>
                <Typography variant="body2">
                  <FormattedMessage id="containers.settings.extensions.createExtension.webhook.sharedSecret.label" />
                </Typography>
                <Typography
                  variant="body2"
                  color="secondary"
                  sx={{
                    a: {
                      color: t => t.palette.text.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.webhook.sharedSecret.subLabel"
                    values={{ link: link(docsLinks.validatingPayloads) }}
                  />
                </Typography>
              </Stack>
              <Typography variant="body2" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.description.fieldDescription" />
              </Typography>
            </Stack>
            <ValidationInput<ExtensionData>
              autoComplete="new-password"
              name="secret"
              type={showSecret ? 'text' : 'password'}
              rightIconProps={{
                onClick: () => setShowSecret(!showSecret),
                'data-cy': 'eye-icon',
              }}
              rightIcon={
                <SvgIcon fontSize="large" color="secondary">
                  {showSecret ? <IconEyeOff /> : <IconEye />}
                </SvgIcon>
              }
              dataCy="extensions-webhook-secret-input"
              control={control}
              getErrorMessage={(_, { message }) => message ?? ''}
              placeholder={intl.formatMessage({
                id: `containers.settings.extensions.createExtension.webhook.secret.placeholder`,
              })}
            />
          </Stack>
        </>
      )}
    </>
  );
};
